import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your payment...');
  const [creditsAdded, setCreditsAdded] = useState(0);

  useEffect(() => {
    confirmPayment();
  }, []);

  const confirmPayment = async () => {
    try {
      // Get transaction ID from sessionStorage
      const transactionId = sessionStorage.getItem('pending_transaction_id');

      if (!transactionId) {
        setStatus('error');
        setMessage('No transaction found. Please contact support.');
        return;
      }

      // Poll for payment confirmation (up to 5 attempts)
      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000; // 2 seconds

      const pollPayment = async () => {
        if (attempts >= maxAttempts) {
          setStatus('error');
          setMessage('Payment verification timed out. Please check your account or contact support.');
          return;
        }

        try {
          // Confirm the payment
          const response = await axios.post(`${API}/payments/confirm/${transactionId}`);

          if (response.data.credits_added) {
            setStatus('success');
            setMessage(response.data.message);
            setCreditsAdded(response.data.credits_added);
            
            // Clear transaction ID
            sessionStorage.removeItem('pending_transaction_id');
            
            // Refresh user data
            await refreshUser();
            
            toast.success(`${response.data.credits_added} credits added successfully!`);
          } else {
            // Payment not yet confirmed, try again
            attempts++;
            setTimeout(pollPayment, pollInterval);
          }
        } catch (error) {
          if (error.response?.status === 404) {
            setStatus('error');
            setMessage('Transaction not found. Please contact support.');
          } else {
            // Retry
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(pollPayment, pollInterval);
            } else {
              setStatus('error');
              setMessage('Unable to verify payment. Please contact support with your transaction details.');
            }
          }
        }
      };

      // Start polling
      pollPayment();
    } catch (error) {
      console.error('Payment confirmation error:', error);
      setStatus('error');
      setMessage('An error occurred. Please contact support.');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto py-12">
        <Card className="glass-card">
          <CardHeader className="text-center">
            {status === 'processing' && (
              <div className="flex flex-col items-center space-y-4">
                <Loader2 className="h-16 w-16 animate-spin" style={{ color: '#C49603' }} />
                <CardTitle className="text-2xl" style={{ color: '#245451' }}>
                  Processing Payment
                </CardTitle>
                <CardDescription style={{ color: '#64748B' }}>
                  Please wait while we confirm your payment...
                </CardDescription>
              </div>
            )}

            {status === 'success' && (
              <div className="flex flex-col items-center space-y-4">
                <div 
                  className="p-4 rounded-full"
                  style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
                >
                  <CheckCircle2 className="h-16 w-16" style={{ color: '#22C55E' }} />
                </div>
                <CardTitle className="text-2xl" style={{ color: '#245451' }}>
                  Payment Successful!
                </CardTitle>
                <CardDescription className="text-lg" style={{ color: '#64748B' }}>
                  {message}
                </CardDescription>
              </div>
            )}

            {status === 'error' && (
              <div className="flex flex-col items-center space-y-4">
                <div 
                  className="p-4 rounded-full"
                  style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
                >
                  <XCircle className="h-16 w-16" style={{ color: '#EF4444' }} />
                </div>
                <CardTitle className="text-2xl" style={{ color: '#DC2626' }}>
                  Payment Issue
                </CardTitle>
                <CardDescription style={{ color: '#64748B' }}>
                  {message}
                </CardDescription>
              </div>
            )}
          </CardHeader>

          <CardContent className="text-center space-y-4">
            {status === 'success' && (
              <>
                <div 
                  className="p-6 rounded-lg"
                  style={{ backgroundColor: 'rgba(196, 150, 3, 0.1)' }}
                >
                  <p className="text-sm" style={{ color: '#64748B' }}>Credits Added</p>
                  <p className="text-4xl font-bold mt-2" style={{ color: '#C49603' }}>
                    +{creditsAdded}
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/dashboard')}
                  size="lg"
                  className="w-full"
                  style={{ backgroundColor: '#245451' }}
                >
                  Go to Dashboard
                </Button>
              </>
            )}

            {status === 'error' && (
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/settings')}
                  size="lg"
                  className="w-full"
                  style={{ backgroundColor: '#245451' }}
                >
                  Back to Settings
                </Button>
                <Button
                  onClick={() => window.location.href = 'mailto:support@prelitx.com'}
                  variant="outline"
                  size="lg"
                  className="w-full"
                  style={{ borderColor: '#E2E8F0', color: '#245451' }}
                >
                  Contact Support
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PaymentSuccessPage;
